// Currency conversion utilities
// Note: This uses CoinGecko's free API which has rate limits
// For production, consider caching rates aggressively

const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  eur: '€',
  gbp: '£',
  jpy: '¥',
  cny: '¥',
  cad: 'C$',
  aud: 'A$',
  chf: 'CHF',
  inr: '₹',
  btc: '₿',
  eth: 'Ξ',
};

const CURRENCY_NAMES: Record<string, string> = {
  usd: 'US Dollar',
  eur: 'Euro',
  gbp: 'British Pound',
  jpy: 'Japanese Yen',
  cny: 'Chinese Yuan',
  cad: 'Canadian Dollar',
  aud: 'Australian Dollar',
  chf: 'Swiss Franc',
  inr: 'Indian Rupee',
  btc: 'Bitcoin',
  eth: 'Ethereum',
};

// Cache for exchange rates (5 minute cache)
let exchangeRateCache: { [key: string]: { rate: number; timestamp: number } } = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency.toLowerCase()] || currency.toUpperCase();
}

export function getCurrencyName(currency: string): string {
  return CURRENCY_NAMES[currency.toLowerCase()] || currency.toUpperCase();
}

export function formatCurrency(
  amount: number | null | undefined,
  currency: string = 'usd',
  options?: { compact?: boolean; decimals?: number }
): string {
  const symbol = getCurrencySymbol(currency);
  const { compact = false, decimals } = options || {};

  if (amount == null || isNaN(amount) || !isFinite(amount)) {
    return `${symbol}-`;
  }

  if (compact) {
    if (Math.abs(amount) >= 1e12) return `${symbol}${(amount / 1e12).toFixed(2)}T`;
    if (Math.abs(amount) >= 1e9) return `${symbol}${(amount / 1e9).toFixed(2)}B`;
    if (Math.abs(amount) >= 1e6) return `${symbol}${(amount / 1e6).toFixed(2)}M`;
    if (Math.abs(amount) >= 1e3) return `${symbol}${(amount / 1e3).toFixed(2)}K`;
  }

  const dec = decimals !== undefined ? decimals : (Math.abs(amount) < 1 ? 4 : 2);
  return `${symbol}${amount.toFixed(dec)}`;
}

export async function getExchangeRate(
  fromCurrency: string = 'usd',
  toCurrency: string
): Promise<number> {
  if (fromCurrency.toLowerCase() === toCurrency.toLowerCase()) {
    return 1;
  }

  const cacheKey = `${fromCurrency}_${toCurrency}`;
  const cached = exchangeRateCache[cacheKey];

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.rate;
  }

  try {
    // Use CoinGecko's simple price API to get USD rates, then convert
    // For simplicity, we'll fetch both currencies against USD
    const fromLower = fromCurrency.toLowerCase();
    const toLower = toCurrency.toLowerCase();

    // If converting from USD, we can use CoinGecko directly
    if (fromLower === 'usd') {
      const response = await fetch(
        `https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=${toLower}`
      );
      const data = await response.json();
      
      // Get BTC price in target currency, then get BTC price in USD to calculate rate
      // Actually, let's use a simpler approach: use a free exchange rate API
      // For now, we'll use a fallback rate or fetch from CoinGecko's exchange_rates endpoint
      
      // Try CoinGecko exchange rates (if available) or use a free currency API
      const exchangeResponse = await fetch(
        `https://api.coingecko.com/api/v3/exchange_rates`
      );
      const exchangeData = await exchangeResponse.json();
      
      if (exchangeData.rates && exchangeData.rates[toLower]) {
        const rate = 1 / exchangeData.rates[toLower].value;
        exchangeRateCache[cacheKey] = { rate, timestamp: Date.now() };
        return rate;
      }
    }

    // Fallback: Use a public exchange rate API (e.g., exchangerate-api.com free tier)
    // For now, return 1 (no conversion) if we can't fetch
    // In production, you'd want a more robust solution
    console.warn(`Could not fetch exchange rate for ${fromCurrency} to ${toCurrency}, using 1:1`);
    return 1;
  } catch (error) {
    console.error('Error fetching exchange rate:', error);
    // Return cached rate if available, otherwise 1
    if (cached) return cached.rate;
    return 1;
  }
}

// For crypto prices from CoinGecko, they're already in the requested currency
// We just need to handle formatting
export function convertPrice(
  priceInUSD: number,
  targetCurrency: string,
  exchangeRate: number = 1
): number {
  return priceInUSD * exchangeRate;
}

export const SUPPORTED_CURRENCIES = [
  { code: 'usd', name: 'US Dollar', symbol: '$' },
  { code: 'eur', name: 'Euro', symbol: '€' },
  { code: 'gbp', name: 'British Pound', symbol: '£' },
  { code: 'jpy', name: 'Japanese Yen', symbol: '¥' },
  { code: 'cny', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'cad', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'aud', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'chf', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'inr', name: 'Indian Rupee', symbol: '₹' },
];

